import type { BreakdownNode } from "./breakdown";

export const TRADE_ROW_BASE_INDENT = 8;
export const TRADE_ROW_LEVEL_INDENT = 32;
export const TRADE_ROW_ARROW_INDENT = 24;

export const tradeRowIndent = (level: number, isLeaf: boolean): number => {
  if (level <= 1) {
    return TRADE_ROW_BASE_INDENT;
  }
  const indent = TRADE_ROW_BASE_INDENT + TRADE_ROW_LEVEL_INDENT * (level - 1);
  return isLeaf ? indent + TRADE_ROW_ARROW_INDENT : indent;
};

export const tradeNodeName = (node: BreakdownNode): string =>
  (node.title as string) || (node.label as string) || (node.id as string);

export const tradeNodeMatches = (node: BreakdownNode, query: string): boolean =>
  (node.id as string).toLowerCase().includes(query) || tradeNodeName(node).toLowerCase().includes(query);

export const filterTradeNodesByQuery = <T>(nodes: BreakdownNode<T>[], query: string): BreakdownNode<T>[] =>
  nodes.reduce<BreakdownNode<T>[]>((visible, node) => {
    if (tradeNodeMatches(node, query)) {
      visible.push(node);
    } else if (node.children?.length) {
      const matchingChildren = filterTradeNodesByQuery(node.children, query);
      if (matchingChildren.length) {
        visible.push({ ...node, children: matchingChildren });
      }
    }
    return visible;
  }, []);

export const leafCodesByNode = <T>(rootNodes: BreakdownNode<T>[], isExcluded?: (code: string) => boolean): Record<string, string[]> => {
  const leavesByCode: Record<string, string[]> = {};
  const collect = (node: BreakdownNode<T>): string[] => {
    const code = node.id as string;
    const leaves = node.children?.length
                   ? node.children.flatMap(collect)
                   : (isExcluded?.(code) ? [] : [code]);
    leavesByCode[code] = leaves;
    return leaves;
  };
  rootNodes.forEach(collect);
  return leavesByCode;
};

export const tradeGroupCodes = <T>(rootNodes: BreakdownNode<T>[]): string[] => {
  const codes: string[] = [];
  const collect = (node: BreakdownNode<T>) => {
    if (node.children?.length) {
      codes.push(node.id as string);
      node.children.forEach(collect);
    }
  };
  rootNodes.forEach(collect);
  return codes;
};
